import { useId } from "react";
import { cn } from "@/lib/cn";

/**
 * The lean2automate logo, rendered INLINE from the official files in
 * /public/brand (logo.svg, logo-dark.svg, logo-mark.svg, logo-mark-dark.svg).
 * Paths, colours and text are copied verbatim — never redraw or recolour.
 * Inline (not <img>) so the wordmark renders in the page's Sora/Manrope web fonts
 * (brand guidelines §1.3). Gradient ids are made unique per instance.
 *
 *   <Logo />                       full logo for light surfaces
 *   <Logo tone="dark" />           full logo for navy / dark surfaces
 *   <Logo variant="mark" />        mark only (collapsed sidebar, "Powered by")
 *   <Logo tone="auto" />           switches with the theme (.dark)
 */
export interface LogoProps {
  variant?: "full" | "mark";
  tone?: "light" | "dark" | "auto";
  className?: string;
  /** Accessible name; pass "" when the logo sits next to visible text. */
  title?: string;
}

const PALETTE = {
  light: { ink: "#17202a", teal: "#0f766e", rule: "rgba(23,32,42,.18)", tagline: "#53616f" },
  dark: { ink: "#f8f6f1", teal: "#2dd4bf", rule: "rgba(248,246,241,.25)", tagline: "#a9b6c6" },
} as const;

function LogoSvg({ variant, tone, className, title }: { variant: "full" | "mark"; tone: "light" | "dark"; className?: string; title: string }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const stroke = `l2a-mark-stroke-${uid}`;
  const word = `l2a-word-fill-${uid}`;
  const c = PALETTE[tone];
  // Mark files keep the light rule colour in both tones (as supplied).
  const rule = variant === "mark" ? PALETTE.light.rule : c.rule;
  const labelled = title !== "";
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={variant === "full" ? "0 0 720 136" : "6 -8 248 136"}
      role={labelled ? "img" : undefined}
      aria-label={labelled ? title : undefined}
      aria-hidden={labelled ? undefined : true}
      className={className}
    >
      <defs>
        <linearGradient id={stroke} x1="24" y1="18" x2="236" y2="106" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={c.ink} />
          <stop offset=".42" stopColor={c.teal} />
          <stop offset=".74" stopColor="#2563eb" />
          <stop offset="1" stopColor="#c0840f" />
        </linearGradient>
        <linearGradient id={word} x1="268" y1="24" x2="650" y2="86" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={c.ink} />
          <stop offset=".5" stopColor={c.teal} />
          <stop offset="1" stopColor={c.ink} />
        </linearGradient>
      </defs>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M28 25v70h52" stroke={c.ink} strokeWidth="14" />
        <path d="M49 78c26-33 55-47 86-41 23 5 42 21 58 48" stroke={`url(#${stroke})`} strokeWidth="8" />
        <path d="M91 38c8-14 21-21 39-21 20 0 34 11 34 26 0 12-9 23-26 33l-34 19h65" stroke={`url(#${stroke})`} strokeWidth="12" />
        <path d="M179 97l29-73 29 73M191 73h33" stroke={`url(#${stroke})`} strokeWidth="12" />
        <path d="M76 95h128" stroke={rule} strokeWidth="4" />
        <circle cx="28" cy="95" r="6" fill="#0f766e" />
        <circle cx="80" cy="95" r="4" fill="#2563eb" />
        <circle cx="192" cy="86" r="6" fill="#c0840f" />
      </g>
      <text
        x="116"
        y="76"
        textAnchor="middle"
        fill="#f97316"
        fontFamily="Nirmala UI, Mangal, Noto Sans Devanagari, Arial Unicode MS, sans-serif"
        fontSize="36"
        fontWeight="900"
      >
        ॐ
      </text>
      {variant === "full" && (
        <>
          <text x="268" y="59" fill={`url(#${word})`} fontFamily="Sora, Manrope, Arial, sans-serif" fontSize="39" fontWeight="900" letterSpacing="-1.3">
            lean2automate
          </text>
          <text x="272" y="92" fill={c.tagline} fontFamily="Manrope, Arial, sans-serif" fontSize="15" fontWeight="800" letterSpacing="2.2">
            AI ML DIGITAL TWIN AUTOMATION
          </text>
          <path d="M268 109h392" stroke="#0f766e" strokeWidth="3" strokeLinecap="round" opacity=".3" />
        </>
      )}
    </svg>
  );
}

export function Logo({ variant = "full", tone = "auto", className, title }: LogoProps) {
  const label = title ?? (variant === "full" ? "lean2automate logo" : "lean2automate");
  const sizing = cn(variant === "full" ? "h-8 w-auto" : "h-7 w-auto", className);
  if (tone === "auto") {
    return (
      <>
        <LogoSvg variant={variant} tone="light" title={label} className={cn(sizing, "dark:hidden")} />
        <LogoSvg variant={variant} tone="dark" title={label} className={cn(sizing, "hidden dark:block")} />
      </>
    );
  }
  return <LogoSvg variant={variant} tone={tone} title={label} className={sizing} />;
}
